import { useCallback, useEffect, useState } from 'react';

/**
 * Fetches `load()` whenever `requestKey` changes. The previous data is dropped on a new request,
 * so a different site or window never shows stale numbers. A failure, or an answer with no body,
 * sets `failed`, which the views show as an inline "Couldn't load…" with Try again (not the shared
 * error dialog, so a failure is reported once); `retry()` fetches the same request again.
 */
export const useReportFetch = <T>(
  load: () => Promise<T>,
  requestKey: string,
  skip = false
): { data: T | null; loading: boolean; failed: boolean; retry: () => void } => {
  const [loaded, setLoaded] = useState<{ key: string; data: T } | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  // Bumped by retry() to re-run the same request.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (skip) return undefined;
    // Ignore a superseded response if the request changes mid-flight.
    let cancelled = false;
    load()
      .then(data => {
        if (cancelled) return;
        // No body is no report: shown as a failed load, never as a blank view.
        if (data === null || data === undefined) {
          setFailedKey(requestKey);
          return;
        }
        setLoaded({ key: requestKey, data });
        setFailedKey(null);
      })
      .catch(() => {
        if (!cancelled) setFailedKey(requestKey);
      });
    return () => {
      cancelled = true;
    };
    // `load` is re-created every render; the request identity is `requestKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, skip, attempt]);

  // Loaded means this request answered; keyed on the request so a new one never shows old data.
  const isLoaded = loaded?.key === requestKey;
  const currentData = isLoaded ? loaded.data : null;
  const failed = !skip && !isLoaded && failedKey === requestKey;
  const retry = useCallback(() => {
    setFailedKey(null);
    setAttempt(current => current + 1);
  }, []);
  return { data: currentData, loading: !skip && !isLoaded && !failed, failed, retry };
};

/** Stable identity for a fetch: the window fields plus any extra scope (site, login). */
export const reportRequestKey = (
  window: { startDate: string; endDate: string; shiftType?: string },
  ...scope: string[]
): string => [...scope, window.startDate, window.endDate, window.shiftType ?? 'both'].join('|');
